'use client';

import { useInViewport } from 'ahooks';
import { type RefObject, useEffect, useRef } from 'react';
import { useIsScreen } from '@/app/hooks';
import { useStore } from '@/app/providers/store';

/**
 * 用于获取飞行目标元素的 hook
 * @returns   RefObject<T>
 */
export const useFlyTarget = (): RefObject<HTMLDivElement | null> => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView] = useInViewport(ref);
  const lastHandledSeqRef = useRef<number | null>(null);
  const { isSmScreen } = useIsScreen();

  const { flyCreatedAt, flyTtlMs, flyConfig, clearFly, overlayFlyToElement, flyEndId, overlaySourceEl, flySeq } =
    useStore((state) => ({
      flyCreatedAt: state.flyCreatedAt,
      flyTtlMs: state.flyTtlMs,
      flyConfig: state.flyConfig,
      clearFly: state.clearFly,
      overlayFlyToElement: state.overlayFlyToElement,
      flyEndId: state.flyEndId,
      overlaySourceEl: state.overlaySourceEl,
      flySeq: state.flySeq,
    }));

  const [sourceInView] = useInViewport(() => overlaySourceEl as Element | null);

  useEffect(() => {
    if (isSmScreen || !ref.current || !flyCreatedAt || !flyEndId) return;
    if (Date.now() - flyCreatedAt > flyTtlMs) {
      clearFly();
      return;
    }

    // 目标与来源都需可见
    if (!inView) return;
    if (overlaySourceEl && !sourceInView) return;
    // 单次触发保护：同一个 seq 仅触发一次
    if (lastHandledSeqRef.current === flySeq) return;
    overlayFlyToElement(ref.current, flyConfig || undefined);
    lastHandledSeqRef.current = flySeq;
  }, [inView, flyCreatedAt, flyTtlMs, flyConfig, flyEndId, overlaySourceEl, sourceInView, flySeq, isSmScreen]);

  return ref;
};
